// Registration as the Registration App v3 prototype lays it out: one phone screen per step.
//   1 How will you get there?  2 Flying details (flyers only)  3 Who's travelling, who shares a room  4 Badges
// Copy follows the prototype; the key and the event come from the API.
import React, { useMemo, useState } from "react";
import { api, ApiError, newIdempotencyKey } from "../lib/api.js";

const blank = (seed = {}) => ({ name: "", email: "", phone: "", departCity: "", room: 1, badge: true, ...seed });

export default function RepRegister({ account, invitation, inviteKey, onDone }) {
  const idemKey = useMemo(newIdempotencyKey, []);
  const ev = invitation?.event ?? {};
  const prefixes = ev.codePrefixes ?? {};
  const prefix = (m) => (prefixes.rep && prefixes.rep[m]) ?? prefixes[m] ?? "";
  const maxTravellers = Math.max(1, invitation?.travellersAllowed ?? 1);
  const maxBadges = Math.max(0, invitation?.badgesAllowed ?? maxTravellers);
  const where = (ev.location ?? "").split(",")[0].trim();

  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("");
  const [travellers, setTravellers] = useState([blank({ name: account?.name ?? "", email: account?.username ?? "" })]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const flying = method === "B";
  const steps = flying ? ["Travel", "Flying", "Travellers", "Badges"] : ["Travel", "Travellers", "Badges"];
  const stepIndex = (name) => steps.indexOf(name) + 1;
  const update = (i, field, value) => setTravellers(ts => ts.map((t, j) => (j === i ? { ...t, [field]: value } : t)));
  const primary = travellers[0];

  // Rooms: group traveller indexes by the room each picked. One code per room.
  const roomNumbers = [...new Set(travellers.map(t => Number(t.room) || 1))].sort((a, b) => a - b);
  const groups = roomNumbers.map(n => travellers.map((t, i) => (Number(t.room) || 1) === n ? i : -1).filter(i => i >= 0));
  const badgeCount = travellers.filter(t => t.badge).length;

  const flyBlocked = flying && (!primary.departCity.trim() || !primary.phone.trim());
  const travellersBlocked = travellers.some(t => t.name.trim().length < 2 || !t.email.includes("@")) || (flying && travellers.some(t => !t.departCity.trim()));

  async function submit() {
    setError(null); setBusy(true);
    try {
      const body = {
        invitationKey: inviteKey, travelMethod: method,
        ...(travellers.length > 1 && { rooms: groups }),
        travellers: travellers.map(t => ({ name: t.name.trim(), email: t.email.trim(), ...(t.phone.trim() && { phone: t.phone.trim() }), ...(flying && t.departCity.trim() && { departCity: t.departCity.trim() }) })),
        badges: travellers.filter(t => t.badge).map(t => ({ name: t.name.trim(), email: t.email.trim() })),
      };
      onDone(await api("/registrations", { method: "POST", body, idempotencyKey: idemKey }));
    } catch (err) {
      setError(err instanceof ApiError && err.details?.fieldErrors ? Object.values(err.details.fieldErrors).flat()[0] : (err.message || "Could not register. Nothing was saved; try again."));
    } finally { setBusy(false); }
  }

  const Progress = ({ name }) => (
    <div className="stepbar" aria-label={`Step ${stepIndex(name)} of ${steps.length}`}>
      {steps.map((s, i) => <span key={s} className={`seg ${i + 1 <= stepIndex(name) ? "on" : ""}`} />)}
      <span className="steplabel">Step {stepIndex(name)} of {steps.length}</span>
    </div>
  );
  const Back = ({ to }) => <button type="button" className="ghost back" onClick={() => { setError(null); setStep(to); }}>Back</button>;

  // ---- 1 · travel method
  if (step === 1) return (
    <>
      <p className="eyebrow">Your invitation · {invitation?.displayName}</p>
      <h1>Register for the {ev.name ?? "event"}</h1>
      <p className="lead">{[ev.when, ev.location].filter(Boolean).join(", ") || "Dates to be confirmed"}. Two minutes, and nothing to make up.</p>
      <Progress name="Travel" />
      <h2>How will you get to {where || "the event"}?</h2>
      <p className="lead">This sets the letters on your travel code, which is how the agent knows what to book. Driving gives you {prefix("A")}; fly and it becomes {prefix("B")}.</p>
      <div className="choices" role="radiogroup">
        {[["A", "Driving", `Code starts with ${prefix("A")}.`], ["B", "Flying", `Code starts with ${prefix("B")}. The agent will call for flight details.`]].map(([v, label, detail]) => (
          <label key={v} className={`choice ${method === v ? "selected" : ""}`}>
            <input type="radio" name="method" value={v} checked={method === v} onChange={() => setMethod(v)} />
            <span><b>{label}</b><small>{detail}</small></span>
          </label>
        ))}
      </div>
      <button className="primary" type="button" disabled={!method} onClick={() => setStep(2)}>{!method ? "Pick one to carry on" : flying ? "Next: flying details" : "Next: who's travelling"}</button>
    </>
  );

  // ---- 2 · flying details
  if (step === 2 && flying) return (
    <>
      <Back to={1} />
      <Progress name="Flying" />
      <h1>A few more things, because you're flying</h1>
      <p className="lead">Enough for the agent to start work. He will call you for the rest.</p>
      <div className="card">
        <label>Flying from<input type="text" value={primary.departCity} onChange={e => update(0, "departCity", e.target.value)} placeholder="City or airport" autoFocus /></label>
        <label>Mobile <span className="help">So the agent can reach you</span><input type="tel" value={primary.phone} onChange={e => update(0, "phone", e.target.value)} autoComplete="tel" inputMode="tel" /></label>
      </div>
      <div className="card quiet">
        <b>What we deliberately don't ask</b>
        <p className="help">Driver's licence, date of birth, and the flights themselves. The agent collects those when he books, and he is the one who holds them. Rooms are his too; there is no hotel booking in here.</p>
      </div>
      <button className="primary" type="button" disabled={flyBlocked} onClick={() => setStep(3)}>{flyBlocked ? "Fill in the two above" : "Next: who's travelling"}</button>
    </>
  );

  // ---- 3 · travellers and rooms
  if (step === (flying ? 3 : 2)) return (
    <>
      <Back to={flying ? 2 : 1} />
      <Progress name="Travellers" />
      <h1>{maxTravellers === 1 ? "Who's travelling?" : "Who's travelling, and who shares a room?"}</h1>
      <p className="lead">{maxTravellers === 1 ? "This invitation covers one traveller." : `This invitation covers ${maxTravellers} travellers. Share a room, share a code.`} Each person needs their own email, because that is where their badge goes.</p>
      {travellers.map((t, i) => (
        <div className="card traveller" key={i}>
          {travellers.length > 1 && <button type="button" className="ghost remove" onClick={() => setTravellers(ts => ts.filter((_, j) => j !== i))}>Remove</button>}
          <label>Name<input type="text" value={t.name} onChange={e => update(i, "name", e.target.value)} autoComplete={i === 0 ? "name" : "off"} /></label>
          <label>Email<input type="email" value={t.email} onChange={e => update(i, "email", e.target.value)} inputMode="email" /></label>
          {i > 0 && flying && <label>Flying from<input type="text" value={t.departCity} onChange={e => update(i, "departCity", e.target.value)} placeholder={primary.departCity || "City or airport"} /></label>}
          {travellers.length > 1 && (
            <label>Sharing which room?
              <div className="room-pick">{Array.from({ length: travellers.length }, (_, n) => n + 1).map(n => (
                <button type="button" key={n} className={`chip ${(Number(t.room) || 1) === n ? "on" : ""}`} onClick={() => update(i, "room", n)}>Room {n}</button>))}</div>
            </label>
          )}
        </div>
      ))}
      {travellers.length < maxTravellers && <button type="button" className="add" onClick={() => setTravellers(ts => [...ts, blank({ departCity: primary.departCity })])}>Add a room-mate</button>}
      {travellers.length > 1 && (
        <div className="card quiet">
          <b>{groups.length === 1 ? "One code" : `${groups.length} codes, one per room`}</b>
          {groups.map((g, k) => <div className="help" key={k}>Room {roomNumbers[k]}: {g.map(i => travellers[i].name.trim() || "(name)").join(", ")}</div>)}
          <p className="help">One code per room, however many names are against it.</p>
        </div>
      )}
      <button className="primary" type="button" disabled={travellersBlocked} onClick={() => setStep(flying ? 4 : 3)}>{travellersBlocked ? "Name at least one traveller" : "Next: badges"}</button>
    </>
  );

  // ---- 4 · badges
  return (
    <>
      <Back to={flying ? 3 : 2} />
      <Progress name="Badges" />
      <h1>Who needs a badge?</h1>
      <p className="lead">You have {maxBadges} {maxBadges === 1 ? "badge" : "badges"} included. We collect this once and send it straight to the badge system.</p>
      <div className="card">
        {travellers.map((t, i) => (
          <label key={i} className={`choice ${t.badge ? "selected" : ""}`}>
            <input type="checkbox" checked={t.badge} disabled={!t.badge && badgeCount >= maxBadges} onChange={e => update(i, "badge", e.target.checked)} />
            <span><b>{t.name.trim() || "(name)"}</b><small>{t.email.trim()}</small></span>
          </label>
        ))}
        {badgeCount > maxBadges && <div className="error">Only {maxBadges} badges are included on this invitation.</div>}
      </div>
      <p className="help">Collected once, here. Each person needs their own address; that is where their badge goes.</p>
      {error && <div className="error" role="alert">{error}</div>}
      <button className="primary" type="button" disabled={busy || badgeCount > maxBadges} onClick={submit}>{busy ? "Registering…" : "Register and get my code"}</button>
    </>
  );
}
